import React from 'react';

import { makeStyles } from 'tss-react/mui';

import { isSameDay, isSameYear } from 'date-fns';

import { NO_START } from '@ha/date';
import { DateTimeFormat } from '@ha/intl';
import { LocalDate } from '@ha/utilities/Dates/LocalDate';
import { Typography } from '@hbf/dsl/core';

import { useIntl } from 'ha/i18n';
import { ListingCardModel } from 'ha/models/Listing/types';

const useStyles = makeStyles()(theme => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.utils.spacing('ref/spacing/1'),
  },
  marker: {
    width: theme.utils.calculateSize(1.5),
    height: theme.utils.calculateSize(1.5),
    borderRadius: theme.utils.borderRadius('ref/radius/full'),
    flexShrink: 0,

    backgroundColor: theme.palette.secondary.main,
  },
  markerAvailableNow: {
    backgroundColor: theme.palette.success.main,
  },
}));

export const ListingCardDetailsAvailability = React.memo(
  ({ listing }: { listing: ListingCardModel }) => {
    const { T, formatDateTime } = useIntl();

    const { classes, cx } = useStyles();

    const bookableFrom = listing.availability.bookableFrom;
    const availableFromDate =
      !bookableFrom || bookableFrom === NO_START
        ? LocalDate.today()
        : (LocalDate.fromString(bookableFrom) ?? LocalDate.today());

    const isAvailableNow = isSameDay(LocalDate.today(), availableFromDate);
    const isAvailableThisYear = isSameYear(
      LocalDate.today(),
      availableFromDate,
    );

    const date = formatDateTime(
      availableFromDate,
      isAvailableThisYear
        ? DateTimeFormat.DATE_MONTH_DAY_LONG
        : DateTimeFormat.DATE_LONG,
    );

    return (
      <div
        className={classes.container}
        data-test-locator="ListingCard/Availability"
      >
        <div
          className={cx(
            classes.marker,
            isAvailableNow && classes.markerAvailableNow,
          )}
        />

        {isAvailableNow && (
          <Typography variant="body/sm-semibold">
            {T('listing_card.available_now')}
          </Typography>
        )}

        {isAvailableNow || (
          <div>
            <Typography variant="body/sm-regular" color="secondary.light">
              {T('Available from')}{' '}
            </Typography>
            <Typography variant="body/sm-semibold">{date}</Typography>
          </div>
        )}
      </div>
    );
  },
);
